import { Signal } from 'typed-signals';
import type { Container } from 'pixi.js';
import { Md5 } from 'ts-md5';
import cloneDeep from 'lodash/cloneDeep';
import type { LevelData } from '../game/level/LevelData';
import { StrategyPrioritizeColors } from '../game/solver/strategies/StrategyPrioritizeColors';
import { StrategyPrioritizeV2 } from '../game/solver/strategies/StrategyPrioritizeV2';
import { StrategyPrioritizeV3 } from '../game/solver/strategies/StrategyPrioritizeV3';
import { Solver } from '../game/solver/Solver';
import type { SolverStrategy } from '../game/solver/types';
import MathUtils from '../utils/MathUtils';

export class EditorWinRateCalculator {
  public onWinRateUpdated = new Signal<(label: string) => void>();

  private currentHash = '';

  private currentLevelData: LevelData | null = null;

  private paused = false;

  private readonly strategies: { strategy: SolverStrategy; won: number; lost: number; movesFromWonRuns: number[] }[] = [
    { strategy: new StrategyPrioritizeColors(), won: 0, lost: 0, movesFromWonRuns: [] },
    { strategy: new StrategyPrioritizeV2(), won: 0, lost: 0, movesFromWonRuns: [] },
    { strategy: new StrategyPrioritizeV3(), won: 0, lost: 0, movesFromWonRuns: [] },
  ];

  constructor(liveCycleObject: Container, levelData: LevelData) {
    let i = 0;
    const update = (): void => {
      this.update();
      i += 1;
      if (i % 30 === 0) {
        this.emitWinRate();
      }
    };

    const callback = (event: KeyboardEvent): void => {
      if (event.key !== ' ') return;
      this.paused = !this.paused;
    };

    window.addEventListener('keydown', callback);

    liveCycleObject.pixi.ticker.add(update);
    liveCycleObject.on('destroyed', () => {
      liveCycleObject.pixi.ticker.remove(update);
      window.removeEventListener('keydown', callback);
    });

    this.updateLevel(levelData);
  }

  public updateLevel(level: LevelData): void {
    const hash = Md5.hashStr(JSON.stringify(level));
    if (this.currentHash === hash) return;

    this.currentLevelData = level;
    this.currentHash = hash;

    this.strategies.forEach((entry) => {
      entry.won = 0;
      entry.lost = 0;
      entry.movesFromWonRuns.length = 0;
    });
  }

  public emitWinRate(): void {
    if (this.currentLevelData === null) {
      this.onWinRateUpdated.emit('---');
      return;
    }

    const strategiesWinRates = this.strategies.map((entry) => entry.won / (entry.won + entry.lost));
    const strategiesWinMoves = this.strategies.map((entry) => MathUtils.getAverage(entry.movesFromWonRuns));
    const averageWinRate = MathUtils.getAverage(strategiesWinRates);
    const strategiesAvgMoves = MathUtils.getAverage(strategiesWinMoves.filter((v) => v > 0));

    const lines: string[] = [];

    const strategiesWinRateLabel = strategiesWinRates.map((v) => `${(v * 100).toFixed(2)}%`).join(' / ');
    const averageWinRateLabel = `(Avg: ${(averageWinRate * 100).toFixed(2)}%)`;
    lines.push(`Win rate: ${strategiesWinRateLabel} ${averageWinRateLabel} ${this.paused ? '(PAUSED)' : ''}`);

    const movesLabel = strategiesWinMoves.map((v) => v.toFixed(2)).join(' / ');
    const averageMovesLabel = `(Avg: ${strategiesAvgMoves.toFixed(2)})`;
    lines.push(`Moves: ${movesLabel} ${averageMovesLabel}`);

    this.onWinRateUpdated.emit(lines.join('\n'));
  }

  public update(): void {
    if (this.paused) return;

    if (this.strategies[0].won + this.strategies[0].lost >= 1000) return;

    const levelData = this.currentLevelData;

    if (!levelData) return;

    this.strategies.forEach((strategyEntry) => {
      const result = new Solver(strategyEntry.strategy, cloneDeep(levelData)).start();

      if (result.resolved) {
        strategyEntry.won += 1;
        strategyEntry.movesFromWonRuns.push(result.movesMade);
      } else {
        strategyEntry.lost += 1;
      }
    });
  }
}
